import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  Optional,
  Self,
  OnDestroy,
  HostListener,
  ContentChild,
  TemplateRef,
  NgZone,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkOverlayOrigin, ScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { ESCAPE, ENTER, SPACE, UP_ARROW, DOWN_ARROW, HOME, END, TAB } from '@angular/cdk/keycodes';
import {
  SgFormControlBase,
  subscriptAnimation,
  panelAnimation,
  groupCollapseAnimation,
  DROPDOWN_PANEL_POSITIONS,
} from '../../shared';
import { SgSelectOption, SgSelectOptionGroup } from '../../models/select-option.model';

/** Internal group interface with expand state tracking */
interface SgDropdownOptionGroup<T> extends SgSelectOptionGroup<T> {
  key: string;
  expanded?: boolean;
}

/** Represents a navigable item in the dropdown (either a group header or an option) */
interface NavigableItem<T> {
  type: 'group-header' | 'option';
  group?: SgDropdownOptionGroup<T>;
  option?: SgSelectOption<T>;
}

@Component({
  selector: 'sg-dropdown-2',
  templateUrl: './sg-dropdown-2.component.html',
  styleUrls: ['./sg-dropdown-2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [subscriptAnimation, panelAnimation, groupCollapseAnimation],
  standalone: false,
})
export class SgDropdown2Component<T = any> extends SgFormControlBase<T | T[]> implements OnDestroy {
  static ngAcceptInputType_multiple: BooleanInput;

  /** Display mode for multi-select: 'count' shows "X selected", 'chips' shows stacked chips UI */
  @Input() multiSelectDisplay: 'count' | 'chips' = 'count';

  @ViewChild('trigger', { static: true }) triggerElement!: ElementRef<HTMLDivElement>;
  @ViewChild(CdkOverlayOrigin, { static: true }) overlayOrigin!: CdkOverlayOrigin;
  @ContentChild('optionTemplate') optionTemplate?: TemplateRef<any>;

  @Input() placeholder: string = '';
  @Input() size: 'default' | 'small' = 'default';
  @Input() variant: 'default' | 'secondary' | 'underline' = 'default';

  /** Optional info-tooltip shown next to the floating label (parity with the legacy sg-dropdown). */
  @Input() labelTooltip = '';
  @Input() labelTooltipHeading = '';

  /** Panel width mode: 'trigger' matches trigger width, 'auto' fits content (min = trigger width) */
  @Input() panelWidth: 'trigger' | 'auto' = 'trigger';

  @Input()
  get items(): SgSelectOption<T>[] | SgSelectOptionGroup<T>[] {
    return this._items;
  }
  set items(value: SgSelectOption<T>[] | SgSelectOptionGroup<T>[]) {
    this._items = value || []; // Default to empty array if null/undefined
    this._normalizedItemsCache = null; // Invalidate cache when items change
  }
  private _items: SgSelectOption<T>[] | SgSelectOptionGroup<T>[] = [];

  /** Function to compare option values. Defaults to strict equality. */
  @Input() compareWith: (o1: T, o2: T) => boolean = (o1, o2) => o1 === o2;

  @Input()
  get multiple(): boolean {
    return this._multiple;
  }
  set multiple(value: BooleanInput) {
    this._multiple = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  private _multiple = false;

  @Output() readonly valueChange = new EventEmitter<T | T[]>();
  @Output() readonly opened = new EventEmitter<void>();
  @Output() readonly closed = new EventEmitter<void>();

  isOpen = false;
  highlightedIndex = -1;

  /** Controls whether group collapse animation is enabled (disabled on initial render to prevent animation) */
  groupAnimationDisabled = true;

  /** Tracks expanded state for each group by its key (label or index) */
  private _groupExpandedState: Map<string, boolean> = new Map();

  // CDK Overlay configuration
  readonly positions = DROPDOWN_PANEL_POSITIONS;
  readonly panelClass = 'sg-dropdown-overlay-pane';
  scrollStrategy: ScrollStrategy;

  private _documentClickListener: ((event: MouseEvent) => void) | null = null;
  private _normalizedItemsCache: SgDropdownOptionGroup<T>[] | null = null;

  constructor(
    @Optional() @Self() override ngControl: NgControl | null,
    private overlay: Overlay,
    private ngZone: NgZone
  ) {
    super(ngControl);
    this.scrollStrategy = this.overlay.scrollStrategies.reposition();
  }

  get triggerWidth(): number {
    return this.triggerElement?.nativeElement?.offsetWidth || 200;
  }

  get empty(): boolean {
    if (this._multiple) {
      return !this.value || (Array.isArray(this.value) && this.value.length === 0);
    }

    return this.value === null || this.value === undefined;
  }

  /**
   * Returns the flattened list of options for iteration
   */
  get flattenedOptions(): SgSelectOption<T>[] {
    if (this.isGrouped) {
      return (this.items as SgSelectOptionGroup<T>[]).reduce(
        (acc, group) => [...acc, ...group.options],
        [] as SgSelectOption<T>[]
      );
    }

    return this.items as SgSelectOption<T>[];
  }

  /**
   * Checks if items are grouped
   */
  get isGrouped(): boolean {
    return this.items?.length > 0 && 'options' in this.items[0];
  }

  /**
   * Returns normalized items as groups (even for non-grouped items).
   * Cached to prevent re-creating arrays on every change detection cycle,
   * which would cause Angular to re-render options and interrupt click events.
   */
  get normalizedItems(): SgDropdownOptionGroup<T>[] {
    if (this._normalizedItemsCache) {
      return this._normalizedItemsCache;
    }

    if (this.isGrouped) {
      this._normalizedItemsCache = (this._items as SgSelectOptionGroup<T>[]).map((group, index) => {
        const key = group.label || `group-${index}`;

        return {
          ...group,
          key,
          expanded: this._groupExpandedState.get(key) ?? true,
        };
      });
    } else {
      this._normalizedItemsCache = [
        { key: 'default', label: '', options: this._items as SgSelectOption<T>[], expanded: true },
      ];
    }

    return this._normalizedItemsCache;
  }

  /**
   * Gets the display label for the current selection
   */
  get displayValue(): string {
    if (this.empty) {
      return '';
    }

    if (this._multiple && Array.isArray(this.value)) {
      const selectedOptions = this.flattenedOptions.filter((opt) =>
        (this.value as T[]).some((v) => this.compareWith(v, opt.value))
      );

      return selectedOptions.map((opt) => opt.label).join(', ');
    }

    const selectedOption = this.flattenedOptions.find((opt) => this.compareWith(opt.value, this.value as T));

    return selectedOption?.label || '';
  }

  /**
   * Gets the number of selected items for multi-select
   */
  get selectedCount(): number {
    if (!this._multiple || !Array.isArray(this.value)) {
      return 0;
    }

    return this.value.length;
  }

  /**
   * Gets the last selected option for chips display mode
   */
  get lastSelectedOption(): SgSelectOption<T> | null {
    if (!this._multiple || !Array.isArray(this.value) || this.value.length === 0) {
      return null;
    }

    const lastValue = this.value[this.value.length - 1];

    return this.flattenedOptions.find((opt) => this.compareWith(opt.value, lastValue)) || null;
  }

  /**
   * Gets the count of additional selected items (excluding the last one) for chips display
   */
  get additionalSelectedCount(): number {
    return Math.max(0, this.selectedCount - 1);
  }

  /**
   * Gets all selected options for chips display mode
   */
  get selectedOptions(): SgSelectOption<T>[] {
    if (!this._multiple || !Array.isArray(this.value)) {
      return [];
    }

    return this.flattenedOptions.filter((opt) => (this.value as T[]).some((v) => this.compareWith(v, opt.value)));
  }

  /**
   * Gets a comma-separated list of all selected option labels for tooltip display
   */
  get selectedLabelsTooltip(): string {
    return this.selectedOptions.map((opt) => opt.label).join(', ');
  }

  /**
   * Returns a flat list of navigable items including group headers (when grouped) and options.
   * Used for keyboard navigation to allow focusing on group headers.
   */
  get navigableItems(): NavigableItem<T>[] {
    const items: NavigableItem<T>[] = [];

    for (const group of this.normalizedItems) {
      // Add group header as navigable item if grouped and has a label
      if (this.isGrouped && group.label) {
        items.push({ type: 'group-header', group });
      }

      // Add options only if group is expanded
      if (group.expanded !== false) {
        for (const option of group.options) {
          items.push({ type: 'option', option, group });
        }
      }
    }

    return items;
  }

  /**
   * Returns the currently highlighted navigable item
   */
  get highlightedItem(): NavigableItem<T> | null {
    if (this.highlightedIndex < 0 || this.highlightedIndex >= this.navigableItems.length) {
      return null;
    }

    return this.navigableItems[this.highlightedIndex];
  }

  /**
   * Checks if a group header is currently highlighted
   */
  isGroupHighlighted(group: SgDropdownOptionGroup<T>): boolean {
    const item = this.highlightedItem;

    return item?.type === 'group-header' && item.group?.key === group.key;
  }

  /**
   * Gets the navigable index for an option
   */
  getNavigableIndex(groupIndex: number, optionIndex: number): number {
    const items = this.navigableItems;
    const groups = this.normalizedItems;
    const targetGroup = groups[groupIndex];
    const targetOption = targetGroup?.options[optionIndex];

    if (!targetOption) {
      return -1;
    }

    return items.findIndex((item) => item.type === 'option' && item.option === targetOption);
  }

  /**
   * Gets the navigable index for a group header
   */
  getGroupNavigableIndex(group: SgDropdownOptionGroup<T>): number {
    return this.navigableItems.findIndex((item) => item.type === 'group-header' && item.group?.key === group.key);
  }

  /**
   * Gets the selection state for the "Select All" checkbox.
   * Returns 'none', 'all', or 'indeterminate'.
   */
  get selectAllState(): 'none' | 'all' | 'indeterminate' {
    if (!this._multiple) {
      return 'none';
    }

    const totalOptions = this.flattenedOptions.length;
    const selectedCount = this.selectedCount;

    if (selectedCount === 0) {
      return 'none';
    }

    if (selectedCount === totalOptions) {
      return 'all';
    }

    return 'indeterminate';
  }

  /**
   * Gets the appropriate checkbox icon for the "Select All" row.
   */
  get selectAllIcon(): string {
    switch (this.selectAllState) {
      case 'all':
        return 'check_box';
      case 'indeterminate':
        return 'indeterminate_check_box';
      default:
        return 'check_box_outline_blank';
    }
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.disabled || this.isOpen) {
      return;
    }

    this.isOpen = true;
    this.highlightedIndex = this.getInitialHighlightedIndex();
    this.opened.emit();
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.highlightedIndex = -1;
    this._touched = true;
    this.onTouched();
    this.closed.emit();
    this.cdr.markForCheck();
  }

  onOverlayAttached(): void {
    // Add document click listener to close dropdown when clicking outside
    this.ngZone.runOutsideAngular(() => {
      // Use setTimeout to avoid the current click event triggering the close
      setTimeout(() => {
        this._documentClickListener = (event: MouseEvent) => {
          // Check if the click was inside the dropdown trigger or panel
          const clickedInsideTrigger = this.elementRef.nativeElement.contains(event.target as Node);
          const clickedInsidePanel = (event.target as HTMLElement).closest('.sg-dropdown__panel');

          if (!clickedInsideTrigger && !clickedInsidePanel) {
            this.ngZone.run(() => this.close());
          }
        };
        document.addEventListener('click', this._documentClickListener);
      });
    });
  }

  onOverlayDetached(): void {
    this.removeDocumentClickListener();
  }

  private removeDocumentClickListener(): void {
    if (this._documentClickListener) {
      document.removeEventListener('click', this._documentClickListener);
      this._documentClickListener = null;
    }
  }

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  selectOption(option: SgSelectOption<T>): void {
    if (this._multiple) {
      this.toggleMultipleOption(option);
    } else {
      this.selectSingleOption(option);
    }
  }

  isSelected(option: SgSelectOption<T>): boolean {
    if (this._multiple && Array.isArray(this.value)) {
      return this.value.some((v) => this.compareWith(v, option.value));
    }

    return this.value != null && this.compareWith(this.value as T, option.value);
  }

  clear(event?: MouseEvent): void {
    event?.stopPropagation();
    this.value = this._multiple ? [] : null;
    this.onChange(this.value);
    this.valueChange.emit(this.value as T | T[]);
    this.cdr.markForCheck();
  }

  /**
   * Clears all selected items except the last one (used by stacked chip X button)
   */
  clearAllButLast(event: MouseEvent): void {
    event.stopPropagation();

    if (!this._multiple || !Array.isArray(this.value) || this.value.length <= 1) {
      return;
    }

    const lastValue = this.value[this.value.length - 1];
    this.value = [lastValue];
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.cdr.markForCheck();
  }

  removeChip(option: SgSelectOption<T>, event: MouseEvent): void {
    event.stopPropagation();

    if (!this._multiple || !Array.isArray(this.value)) {
      return;
    }

    const currentValue = [...this.value];
    const index = currentValue.findIndex((v) => this.compareWith(v, option.value));

    if (index >= 0) {
      currentValue.splice(index, 1);
      this.value = currentValue;
      this.onChange(this.value);
      this.valueChange.emit(this.value);
      this.cdr.markForCheck();
    }
  }

  toggleSelectAll(): void {
    if (!this._multiple) {
      return;
    }

    if (this.selectAllState === 'all') {
      this.value = [];
    } else {
      this.value = this.flattenedOptions.map((opt) => opt.value);
    }

    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.cdr.markForCheck();
  }

  /**
   * Toggles the expanded/collapsed state of a group.
   */
  toggleGroup(group: SgDropdownOptionGroup<T>): void {
    // Enable animation temporarily
    this.groupAnimationDisabled = false;
    this.cdr.detectChanges();

    // Toggle the expanded state
    const newExpanded = group.expanded === false;
    this._groupExpandedState.set(group.key, newExpanded);

    // Invalidate cache to rebuild with new expanded state
    this._normalizedItemsCache = null;
    this.cdr.markForCheck();

    // Disable animation after transition completes
    setTimeout(() => {
      this.groupAnimationDisabled = true;
    }, 250);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    const keyCode = event.keyCode;

    if (!this.isOpen) {
      if (keyCode === ENTER || keyCode === SPACE || keyCode === DOWN_ARROW) {
        event.preventDefault();
        this.open();
      }

      return;
    }

    switch (keyCode) {
      case ESCAPE:
        event.preventDefault();
        this.close();
        break;

      case TAB:
        this.close();
        break;

      case ENTER:
      case SPACE:
        event.preventDefault();
        if (this.highlightedIndex >= 0) {
          const highlightedItem = this.highlightedItem;
          if (highlightedItem?.type === 'group-header' && highlightedItem.group) {
            this.toggleGroup(highlightedItem.group);
          } else if (highlightedItem?.type === 'option' && highlightedItem.option) {
            this.selectOption(highlightedItem.option);
          }
        }
        break;

      case UP_ARROW:
        event.preventDefault();
        this.highlightPrevious();
        break;

      case DOWN_ARROW:
        event.preventDefault();
        this.highlightNext();
        break;

      case HOME:
        event.preventDefault();
        this.highlightFirst();
        break;

      case END:
        event.preventDefault();
        this.highlightLast();
        break;
    }
  }

  writeValue(value: T | T[] | null): void {
    this._value = value as (T | T[] | null) & (T | null);
    this.cdr.markForCheck();
  }

  override ngOnDestroy(): void {
    this.removeDocumentClickListener();
    super.ngOnDestroy();
  }

  private selectSingleOption(option: SgSelectOption<T>): void {
    this.value = option.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.cdr.markForCheck();
    this.close();
  }

  private toggleMultipleOption(option: SgSelectOption<T>): void {
    const currentValue = Array.isArray(this.value) ? [...this.value] : [];
    const index = currentValue.findIndex((v) => this.compareWith(v, option.value));

    if (index >= 0) {
      currentValue.splice(index, 1);
    } else {
      currentValue.push(option.value);
    }

    this.value = currentValue;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.cdr.markForCheck();
  }

  private getInitialHighlightedIndex(): number {
    const items = this.navigableItems;

    if (this.empty) {
      return items.length > 0 ? 0 : -1;
    }

    // Find the first selected option in navigable items
    if (this._multiple && Array.isArray(this.value) && this.value.length > 0) {
      const firstSelectedValue = this.value[0];

      return items.findIndex(
        (item) => item.type === 'option' && item.option && this.compareWith(item.option.value, firstSelectedValue)
      );
    }

    return items.findIndex(
      (item) => item.type === 'option' && item.option && this.compareWith(item.option.value, this.value as T)
    );
  }

  private highlightNext(): void {
    const items = this.navigableItems;
    if (items.length === 0) {
      return;
    }

    this.highlightedIndex = (this.highlightedIndex + 1) % items.length;
    this.cdr.markForCheck();
  }

  private highlightPrevious(): void {
    const items = this.navigableItems;
    if (items.length === 0) {
      return;
    }

    this.highlightedIndex = this.highlightedIndex <= 0 ? items.length - 1 : this.highlightedIndex - 1;
    this.cdr.markForCheck();
  }

  private highlightFirst(): void {
    this.highlightedIndex = 0;
    this.cdr.markForCheck();
  }

  private highlightLast(): void {
    const items = this.navigableItems;
    this.highlightedIndex = items.length - 1;
    this.cdr.markForCheck();
  }

  /**
   * Gets the flat index for an option within a group
   */
  getOptionFlatIndex(groupIndex: number, optionIndex: number): number {
    if (!this.isGrouped) {
      return optionIndex;
    }

    let flatIndex = 0;
    const groups = this.normalizedItems;

    for (let i = 0; i < groupIndex; i++) {
      flatIndex += groups[i].options.length;
    }

    return flatIndex + optionIndex;
  }
}
